import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { useFlightSearch, formatAirports } from "@/lib/flights";
import { TransportData, Trip, TripTransport, TransportLeg } from "@/types/trip";
import { ServerFlightInfo, FlightSearchRequest } from "@/types/flights";
import { format } from "date-fns";
import { AlertCircle, Loader2, SlidersHorizontal } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import FlightCard from "./FlightCard";
import FlightCardSkeleton from "./FlightCardLoading";
import FlightModal from "./FlightModal";
import FilterSidebar from "./FilterSidebar";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { DateTime } from "luxon";
import { AnimatePresence } from "framer-motion";
import { FilterState, defaultFilterState } from "./filterTypes";
import useCurrencyStore from "@/stores/ui/currencyStore";

interface FlightContainerProps {
  transportData: TransportData;
  selectedFlight: TripTransport | null;
  setSelectedFlight: (flight: TripTransport | null) => void;
  addFlightToTrip: (
    trip: Trip,
    flight: ServerFlightInfo,
    transportData: TransportData
  ) => void;
  flightModalOpen: boolean;
  setFlightModalOpen: (isOpen: boolean) => void;
}

const convertFlightToTripTransport = (
  flight: ServerFlightInfo,
  fromLocationId: string,
  toLocationId: string
): TripTransport => {
  const legs: TransportLeg[] = flight.route.map((route) => ({
    id: `${route.flight_number}-${route.departure_utc}`,
    mode: "flight",
    provider: {
      name: route.airline,
      logo: `https://images.kiwi.com/airlines/128x128/${route.short_airline}.png?default=airline.png`,
      type: "airline",
      code: route.short_airline,
    },
    departure: {
      location: {
        name: route.from.city,
        code: route.from.airport,
        coordinates: {
          latitude: parseFloat(route.from.latitude),
          longitude: parseFloat(route.from.longitude),
        },
        timezone: "UTC",
      },
      time: {
        local: route.departure_local,
        utc: route.departure_utc,
      },
    },
    arrival: {
      location: {
        name: route.to.city,
        code: route.to.airport,
        coordinates: {
          latitude: parseFloat(route.to.latitude),
          longitude: parseFloat(route.to.longitude),
        },
        timezone: "UTC",
      },
      time: {
        local: route.arrival_local,
        utc: route.arrival_utc,
      },
    },
    reference: `${route.short_airline}${route.flight_number}`,
    duration: route.duration,
    booking_info: {
      confirmation: flight.booking_token,
      price: flight.price,
      currency: "USD",
      booking_link: flight.booking_link,
    },
  }));

  return {
    id: flight.id,
    fromLocationId,
    toLocationId,
    type: "api",
    legs,
    total_price: flight.price,
    currency: "USD",
    metadata: {
      api_booking_token: flight.booking_token,
      api_search_id: flight.id,
    },
  };
};

const FlightContainer: React.FC<FlightContainerProps> = ({
  transportData,
  selectedFlight,
  setSelectedFlight,
  addFlightToTrip,
  flightModalOpen,
  setFlightModalOpen,
}) => {
  const { tripData, selectedLocation, createLocationUpdate, checkIsAdmin } =
    useTripStore();
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);
  const { selectedCurrency } = useCurrencyStore();
  const [filters, setFilters] = useState<FilterState>(defaultFilterState);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { ref, inView } = useInView();
  const { toast } = useToast();
  const [selectedTripTransport, setSelectedTripTransport] =
    useState<TripTransport | null>(null);

  const searchParams = useMemo(
    (): FlightSearchRequest => ({
      fly_from: formatAirports(transportData.locationAbove?.airports || []),
      fly_to: formatAirports(transportData.locationBelow?.airports || []),
      date_from: format(
        new Date(transportData.selectedLocation?.date.start_date || ""),
        "dd/MM/yyyy"
      ),
      date_to: format(
        new Date(transportData.selectedLocation?.date.start_date || ""),
        "dd/MM/yyyy"
      ),
      adults: 1,
      children: 0,
      dtime_to: "23:59",
      selected_cabins: filters.cabinClass || "M",
      curr: selectedCurrency.code,
      locale: "en-US",
      sort:
        filters.sort === "best"
          ? "quality"
          : filters.sort === "cheapest"
          ? "price"
          : "duration",
    }),
    [transportData, filters, selectedCurrency]
  );

  const {
    data: flightSearchResults,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useFlightSearch(searchParams, !!selectedLocation && !!transportData);

  const handleLoadMore = useCallback(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    handleLoadMore();
  }, [handleLoadMore]);

  const handleSelectFlight = useCallback(
    (flight: ServerFlightInfo) => {
      const tripTransport = convertFlightToTripTransport(
        flight,
        transportData.locationAbove?.id || "",
        transportData.locationBelow?.id || ""
      );
      setSelectedFlight(tripTransport);
      setSelectedTripTransport(tripTransport);
      setFlightModalOpen(true);
    },
    [setSelectedFlight, setFlightModalOpen, transportData]
  );

  const handleCloseModal = useCallback(() => {
    setFlightModalOpen(false);
    setSelectedFlight(null);
    setSelectedTripTransport(null);
  }, [setFlightModalOpen, setSelectedFlight]);

  const handleAddFlight = useCallback(
    (flight: ServerFlightInfo) => {
      if (!tripData || !transportData) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Missing trip or transport data",
        });
        return;
      }

      try {
        addFlightToTrip(tripData, flight, transportData);
        createLocationUpdate(
          selectedLocation!.waytora_id,
          selectedLocation!.id,
          "transport_added",
          {
            itemName: flight.to.city,
            itemType: "flight",
            flight: flight,
          }
        );
        toast({
          title: "Flight Added",
          description: `Flight to ${flight.to.city} has been added to your trip`,
        });
      } catch (error) {
        console.error("Error adding flight:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to add flight. Please try again.",
        });
      }
    },
    [
      addFlightToTrip,
      tripData,
      transportData,
      createLocationUpdate,
      selectedLocation,
      toast,
    ]
  );

  const filteredFlights = useMemo(() => {
    if (!flightSearchResults?.pages) return [];

    let flights = flightSearchResults.pages.flatMap(
      (page) => page.data.flights
    );

    // Apply filters
    if (filters.airlines.length > 0) {
      flights = flights.filter((flight) =>
        filters.airlines.includes(flight.route[0]?.airline || "")
      );
    }

    if (filters.maxStops !== null) {
      flights = flights.filter((flight) => flight.stops <= filters.maxStops!);
    }

    if (filters.priceRange) {
      flights = flights.filter(
        (flight) =>
          flight.price >= filters.priceRange!.min &&
          flight.price <= filters.priceRange!.max
      );
    }

    if (filters.departureTime) {
      flights = flights.filter((flight) => {
        const deptTime = DateTime.fromISO(flight.departure_utc).toFormat(
          "HH:mm"
        );
        return (
          deptTime >= filters.departureTime!.start &&
          deptTime <= filters.departureTime!.end
        );
      });
    }

    return flights;
  }, [flightSearchResults?.pages, filters]);

  const availableAirlines = useMemo(() => {
    if (!flightSearchResults?.pages) return [];
    const airlines = new Set<string>();
    flightSearchResults.pages.forEach((page) => {
      page.data.flights.forEach((flight) => {
        if (flight.route[0]?.airline) {
          airlines.add(flight.route[0].airline);
        }
      });
    });
    return Array.from(airlines);
  }, [flightSearchResults?.pages]);

  const priceRange = useMemo(() => {
    if (!flightSearchResults?.pages) return { min: 0, max: 1000 };
    let min = Infinity;
    let max = -Infinity;
    flightSearchResults.pages.forEach((page) => {
      page.data.flights.forEach((flight) => {
        min = Math.min(min, flight.price);
        max = Math.max(max, flight.price);
      });
    });
    return { min: Math.floor(min), max: Math.ceil(max) };
  }, [flightSearchResults?.pages]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.airlines.length > 0) count++;
    if (filters.maxStops !== null) count++;
    if (filters.cabinClass !== null) count++;
    if (filters.priceRange !== null) count++;
    if (filters.departureTime !== null) count++;
    if (filters.arrivalTime !== null) count++;
    return count;
  }, [filters]);

  if (isError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center space-y-4 p-6">
        <AlertCircle className="h-12 w-12 text-rose-500" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-neutral-800">
            Error Loading Flights
          </h3>
          <p className="text-sm text-neutral-600 mt-1">{error.message}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-700 hover:bg-indigo-800 rounded-md transition duration-300 ease-in-out"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Top Bar with Filter Button */}
      <div className="flex items-center justify-between px-4 bg-neutral-100 h-12 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-neutral-600 font-light">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>
                Finding flights from {transportData.locationAbove?.name} to{" "}
                {transportData.locationBelow?.name}
              </span>
            </>
          ) : (
            `${filteredFlights.length} flights found`
          )}
        </div>
        <button
          onClick={() => setIsFilterOpen(true)}
          className="flex items-center space-x-3 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-md transition-colors"
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <p className="w-6 h-6 flex items-center justify-center text-xs bg-indigo-200 rounded-full text-indigo-700">
              {activeFilterCount}
            </p>
          )}
        </button>
      </div>

      {/* Flight List */}
      <div className="flex-1 h-[calc(100vh-4rem)] relative overflow-hidden mt-2">
        <div className="w-full h-full flex flex-col space-y-3 rounded-lg overflow-y-auto py-2 no-scrollbar">
          {isLoading ? (
            Array.from({ length: 6 }, (_, i) => <FlightCardSkeleton key={i} />)
          ) : (
            <>
              {filteredFlights.map((flight: ServerFlightInfo) => (
                <FlightCard
                  key={flight.id}
                  flight={flight}
                  onSelect={handleSelectFlight}
                  onAddFlight={handleAddFlight}
                  arrivalTimeZone={
                    transportData.selectedLocation?.date.time_zone || ""
                  }
                  departureTimeZone={
                    transportData.locationAbove?.date.time_zone || ""
                  }
                  selectedLocation={transportData.selectedLocation!}
                  selectedCurrencyCode={selectedCurrency.code}
                  isAdmin={isAdmin}
                />
              ))}
              {isFetchingNextPage &&
                Array.from({ length: 3 }, (_, i) => (
                  <FlightCardSkeleton key={`loading-${i}`} />
                ))}
              <div ref={ref} style={{ height: 20 }} />
            </>
          )}
        </div>
      </div>

      {/* Filter Sidebar */}
      <FilterSidebar
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onFilterChange={setFilters}
        airlines={availableAirlines}
        priceRange={priceRange}
      />

      {/* Flight Modal */}
      <AnimatePresence mode="wait">
        {flightModalOpen && selectedTripTransport && (
          <FlightModal
            flight={selectedTripTransport}
            isOpen={flightModalOpen}
            handleClose={handleCloseModal}
            transportData={transportData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FlightContainer;
